import {FC, useState, useEffect} from 'react'
import {Card, Row, Col, Spinner, Button, Form, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {MFNTariffModel} from '../../tariff/core/_models'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {
  createTariffByType,
  getHSDetail,
  getTariffByTypeAndID,
  updateTariffByTypeAndID,
} from '../core/_requests'
import {PageTitle} from '../../../../../_metronic/layout/core'
import EconDropdown from '../../../../../ui-kit/EconDropdown'
import HSDropdown from './HSDropdown'
import HSDropdownWithLoadMore from './HSDropdownWithLoadMore'
import HSDescriptionDetailView from './HSDescriptionDetailView'
import { Toast, showSwal } from '../../../../../ui-kit'

const confirmDialog = (title: string) =>
  showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })

const INITIAL_STATE: MFNTariffModel = {
  rep: '',
  hs: '',
  year: 0,
  val: 0,
  src: '',
  n_TL: 0,
  n_AV_TL: 0,
  min_av_duty: 0,
  max_av_duty: 0,
  hs_ver_org: '',
  duty_free_pct: 0,
  n_non_AV_duties: 0,
  non_AV_types: 0,
}

export type MFNTariffForm = {
  subject: string
  type: string
  HSCodes: any[]
  HSVersions: any[]
}

const MFNTariffForm: FC<MFNTariffForm> = ({subject, type, HSCodes, HSVersions}) => {
  const ability = useAbility(AbilityContext)
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const {idTariff} = useParams()

  const isDetailMode = Boolean(idTariff)
  const isAddMode = !idTariff

  const [isEditMode, setIsEditMode] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [hsDescriptionData, setHSDescriptionData] = useState<any>(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [formData, setFormData] = useState<MFNTariffModel>({
    ...INITIAL_STATE,
  })

  const isEditable = isEditMode || isAddMode

  const validateForm = () => {
    const requiredFields: (keyof MFNTariffModel)[] = ['rep', 'hs', 'year', 'val', 'hs_ver_org']
    const isValid = requiredFields.every((key) => {
      const val = formData[key]
      return (
        val !== undefined && val !== null && String(val).trim() !== '0' && String(val).trim() !== ''
      )
    })
    setIsFormValid(isValid)
  }

  useEffect(() => {
    if (!isDetailMode) return
    const fetch = async () => {
      try {
        setIsLoadingData(true)
        const res = await getTariffByTypeAndID(axiosPrivate, type, idTariff)
        if (res?.data?.data) {
          const data = res.data.data
          setFormData(data)

          // Fetch detailed HS description
          try {
            const resDetail = await getHSDetail(axiosPrivate, data.hs, data.hs_version)
            if (resDetail?.data?.data) {
              setHSDescriptionData(resDetail.data.data)
            }
          } catch (err) {
            console.error('Failed to fetch detailed HS description', err)
            Toast(
              'Error when fetching tariff data',
              'danger',
              <Link to={`/master-data-hs-descriptions/add?ver=${data.hs_ver_org}&code=${data.hs}`}>
                Create new HS Description
              </Link>
            )
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingData(false)
      }
    }
    fetch()
  }, [idTariff])

  useEffect(() => {
    validateForm()
  }, [formData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handleSubmit = () => {
    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this MFN Tariff?`
    ).then((result) => {
      if (!result.isConfirmed) return
      setIsLoadingButton(true)

      const action = isEditMode
        ? updateTariffByTypeAndID(axiosPrivate, type, formData)
        : createTariffByType(axiosPrivate, type, formData)

      const successStatus = isEditMode ? 200 : 201
      const successMessage = isEditMode ? 'Data updated' : 'Data created'
      const errorMessage = isEditMode
        ? 'Failed to update MFN Tariff'
        : 'Failed to create MFN Tariff'

      action
        .then((res) => {
          if (res.status === successStatus) {
            showSwal({title: successMessage, icon: 'success', confirmButtonText: 'OK'}).then(
              () => navigate('/tariff/mfn')
            )
          }
        })
        .catch((err) => {
          console.error(err)
          const message = err?.response?.data?.message || errorMessage
          showSwal('Error', message, 'error')
        })
        .finally(() => setIsLoadingButton(false))
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (!result.isConfirmed) return
      if (isEditMode) {
        window.location.reload()
      } else {
        navigate('/master-data-mfn-tariff')
      }
    })
  }

  const ReadOnlyField = ({value}: {value: string | number | undefined}) => (
    <div className='data-value'>{value ?? '-'}</div>
  )

  const fieldLabel = (label: string, required = false) => (
    <Form.Label
      className={`text-primary me-0 ps-0 ${required && isEditable ? 'required' : ''}`}
      column
    >
      {label}
    </Form.Label>
  )

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {
            title: 'Master Data - MFN Tariff',
            path: 'master-data-mfn-tariff',
            isSeparator: false,
            isActive: false,
          },
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isDetailMode ? 'Detail' : 'Add'}
      </PageTitle>

      <div className='MasterDataPage'>
        <Row>
          <Col>
            <Card className='mb-5'>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `MFN Tariff Detail` : 'Add MFN Tariff'}
                      </h3>
                    </div>
                    {!isEditMode && !isAddMode && ability.can('update', subject) && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>

              <Card.Body>
                {isLoadingData ? (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading<span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Form>
                      <div className='row'>
                        {/* REP */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Reporter', true)}
                          {isEditable ? (
                            <EconDropdown
                              econ={formData.rep}
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  rep: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.rep} />
                          )}
                        </div>

                        {/* HS */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('HS Code', true)}
                          {isEditable ? (
                            <HSDropdownWithLoadMore
                              value={formData.hs}
                              placeholder='Select HS Code'
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  hs: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs} />
                          )}
                        </div>

                        {/* HS Ver Org */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('HS Version Origin', true)}
                          {isEditable ? (
                            <HSDropdown
                              items={HSVersions}
                              value={formData.hs_ver_org}
                              placeholder='Select HS Version'
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  hs_ver_org: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs_ver_org} />
                          )}
                        </div>

                        {/* Year */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Year', true)}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter year'
                              type='number'
                              min={0}
                              name='year'
                              value={formData.year}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.year} />
                          )}
                        </div>

                        {/* Val */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Value', true)}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter value'
                              type='number'
                              step='0.01'
                              min={0}
                              name='val'
                              value={formData.val}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.val} />
                          )}
                        </div>

                        {/* Src */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Source')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter source'
                              type='text'
                              name='src'
                              value={formData.src}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.src} />
                          )}
                        </div>

                        {/* n_TL */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Number of Tariff Lines')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter number of tariff lines'
                              type='number'
                              name='n_TL'
                              value={formData.n_TL}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.n_TL} />
                          )}
                        </div>

                        {/* n_AV_TL */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Number of AV Tariff Lines')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter number of AV tariff lines'
                              type='number'
                              name='n_AV_TL'
                              value={formData.n_AV_TL}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.n_AV_TL} />
                          )}
                        </div>

                        {/* min_av_duty */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Min AV Duty')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter minimum AV duty'
                              type='number'
                              name='min_av_duty'
                              value={formData.min_av_duty}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.min_av_duty} />
                          )}
                        </div>

                        {/* max_av_duty */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Max AV Duty')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter maximum AV duty'
                              type='number'
                              name='max_av_duty'
                              value={formData.max_av_duty}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.max_av_duty} />
                          )}
                        </div>

                        {/* duty_free_pct */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Duty Free Percentage')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter duty free percentage'
                              type='number'
                              name='duty_free_pct'
                              value={formData.duty_free_pct}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.duty_free_pct} />
                          )}
                        </div>

                        {/* n_non_AV_duties */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Number of Non-AV Duties')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter number of non-AV duties'
                              type='number'
                              name='n_non_AV_duties'
                              value={formData.n_non_AV_duties}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.n_non_AV_duties} />
                          )}
                        </div>

                        {/* non_AV_types */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Non-AV Types')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter non-AV types'
                              type='text'
                              name='non_AV_types'
                              value={formData.non_AV_types}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.non_AV_types} />
                          )}
                        </div>
                      </div>

                      {isEditable && (
                        <div className='row'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form>

                    {isDetailMode && (
                      <>
                        <hr className='my-10' />
                        <HSDescriptionDetailView hsDescriptionData={hsDescriptionData} />
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default MFNTariffForm
